import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import React, { ComponentProps, useCallback } from 'react'
import styled from 'styled-components'
import RecentSearchSuggestions from '../SearchForm/SearchLocationInput/RecentSearchSuggestions'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import SearchPlaceSelectList from '../SearchPlaceSelect/SearchPlaceSelectList'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { rem } from 'polished'
import Caption from 'LuxKit/Typography/Caption'
import { TypeaheadActionType } from 'analytics/snowplow/events'
import useSearchGridColumns from 'components/SearchV2/Hooks/useSearchGridColunms'
import { handleSectionClick } from 'components/SearchV2/Utils/handleSectionClick'
import { EmptyArray } from 'lib/array/arrayUtils'

const PlaceControl = styled.div`
  width: 100%;

  &:not(.full-width) {
    position: relative;
  }
`

const SearchMenuListItemContainer = styled.div`
  width: ${rem(325)};
  display: flex;
  column-gap: ${rem(40)};
  flex-wrap: wrap;

  &.horizontal {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    width: 100%;
  }
`

const SearchItemContainer = styled.div`
  display: flex;
  column-gap: ${rem(40)};
  flex-wrap: wrap;
`

const StyledCaption = styled(Caption)`
  padding: 0;
`

interface Props {
  /**
   * @default fill-anchor
   */
  size?: ComponentProps<typeof DropdownList>['size']
  className?: string;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  value?: App.SearchItem;
  required?: boolean;
  label: string;
  placeholder: string;
  localInputRef: React.RefObject<HTMLInputElement>;
  searchPhrase: string;
  onSearchPhraseChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleOpen: (e: any) => void;
  onClearClick: () => void;
  finalOpen: boolean;
  closeDropdown: () => void;
  showPlaceholder: boolean;
  recentSearches: Array<App.FullRecentSearch>;
  selectedDropdown: number | undefined;
  onRecentSearchDelete: (item: App.FullRecentSearch) => void;
  onRecentSearchClick: (item: App.FullRecentSearch) => void;
  placeholderTitle?: string;
  placeholdersSearches: Array<App.SearchItem>;
  businessOfficeTitle?: string;
  businessOfficeItems?: Array<App.SearchItem>;
  displayedBusinessOfficeItems?: Array<App.SearchItem>;
  onLocationSelect: (
    searchItem?: App.SearchItem,
    mouseEvent?: React.MouseEvent<HTMLElement>,
    actionType?: TypeaheadActionType,
    position?: App.SearchItemPosition
  ) => void;
  handleResultsChange: (results: Array<App.SearchItem>, searchPhrase: string, numColumns?: number) => void;
  placeTypes?: Array<App.SearchPlaceType>;
  searchTypes?: Array<App.SearchType>;
  searchVertical?: App.SearchPlaceVertical;
  overrideSearchItems?: Array<App.SearchItem>;
  controlsContainerRef?: React.RefObject<HTMLDivElement>;
}

function HotelSearchPlaceSelectDropdown(props: Props) {
  const {
    className,
    onKeyDown,
    value,
    required,
    label,
    placeholder,
    localInputRef,
    searchPhrase,
    onSearchPhraseChange,
    handleOpen,
    onClearClick,
    finalOpen,
    closeDropdown,
    showPlaceholder,
    recentSearches,
    selectedDropdown,
    onRecentSearchDelete,
    onRecentSearchClick,
    placeholderTitle,
    placeholdersSearches,
    businessOfficeTitle,
    businessOfficeItems = EmptyArray,
    displayedBusinessOfficeItems = EmptyArray,
    onLocationSelect,
    handleResultsChange,
    placeTypes,
    searchTypes,
    searchVertical,
    overrideSearchItems,
    controlsContainerRef,
  } = props

  const { containerRef, itemRef, numColumns } = useSearchGridColumns(placeholdersSearches)
  const { containerRef: officesContainerRef, itemRef: officesItemRef, numColumns: officesNumColumns } = useSearchGridColumns(displayedBusinessOfficeItems)

  const handlePopularClick = useCallback(
    (searchItem?: App.SearchItem) => handleSectionClick(searchItem, placeholdersSearches, numColumns, onLocationSelect),
    [placeholdersSearches, numColumns, onLocationSelect],
  )

  const handleOfficeClick = useCallback(
    (searchItem?: App.SearchItem) => handleSectionClick(searchItem, displayedBusinessOfficeItems, officesNumColumns, onLocationSelect),
    [displayedBusinessOfficeItems, officesNumColumns, onLocationSelect],
  )

  return (
    <PlaceControl className={className} onKeyDown={onKeyDown}>
      <HiddenInput
        required={required}
        value={value?.format.mainText}
        invalidMessage="Please select a destination"
      />
      <SearchFormTextField
        label={label}
        placeholder={placeholder}
        ref={localInputRef}
        value={searchPhrase}
        displayValue={value?.format.mainText ?? ''}
        onChange={onSearchPhraseChange}
        onFocus={handleOpen}
        onClear={onClearClick}
      />
      {controlsContainerRef && <DropdownSheet
        size="fill-anchor"
        anchorRef={controlsContainerRef}
        triggerRef={localInputRef}
        open={finalOpen}
        data-testid="place-select"
        onClose={closeDropdown}
      >
        {showPlaceholder &&
          <VerticalSpacer gap={28}>
            {!!recentSearches.length && <RecentSearchSuggestions
              recentSearches={recentSearches}
              onDelete={onRecentSearchDelete}
              selectedDropdown={selectedDropdown}
              iconBackground
              onRecentSearchClick={onRecentSearchClick}
              horizontal
            />}
            {!!displayedBusinessOfficeItems.length && (
              <div>
                {businessOfficeTitle && <StyledCaption variant="large" weight="bold">{businessOfficeTitle}</StyledCaption>}
                <SearchItemContainer ref={officesContainerRef}>
                  {displayedBusinessOfficeItems.map((item, index) => (
                    <SearchMenuListItemContainer key={`${item.value}_${index}`}>
                      <SearchMenuListItem
                        ref={index === 0 ? officesItemRef : undefined}
                        searchItem={item}
                        onClick={handleOfficeClick}
                        selected={index + recentSearches.length === selectedDropdown}
                        horizontal
                        searchVertical={searchVertical}
                        imageId={item.imageId}
                        useImage
                      />
                    </SearchMenuListItemContainer>))}
                </SearchItemContainer>
              </div>
            )}
            <div>
              {placeholderTitle && <StyledCaption variant="large" weight="bold">{placeholderTitle}</StyledCaption>}
              <SearchItemContainer ref={containerRef}>
                {placeholdersSearches.map((item, index) => (
                  <SearchMenuListItemContainer key={`${item.value}_${index}`}>
                    <SearchMenuListItem
                      ref={index === 0 ? itemRef : undefined}
                      searchItem={item}
                      onClick={handlePopularClick}
                      selected={index + recentSearches.length + displayedBusinessOfficeItems.length === selectedDropdown}
                      horizontal
                      searchVertical={searchVertical}
                      imageId={item.imageId}
                      useImage
                    />
                  </SearchMenuListItemContainer>))}
              </SearchItemContainer>
            </div>
          </VerticalSpacer>}
        {!showPlaceholder &&
          <SearchPlaceSelectList
            searchPhrase={searchPhrase}
            onResultsChange={handleResultsChange}
            onChange={onLocationSelect}
            placeTypes={placeTypes}
            searchTypes={searchTypes}
            vertical={searchVertical}
            selectedDropdown={selectedDropdown}
            overrideSearchItems={overrideSearchItems}
            businessOfficeItems={businessOfficeItems}
            showHorizontalLayout
            useImage
          />}
      </DropdownSheet>}
    </PlaceControl>
  )
}

export default HotelSearchPlaceSelectDropdown
